import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useConversation } from '../lib/queries';
import { useQueryClient } from '@tanstack/react-query';
import usePushBus from '../hooks/usePushBus';
import { toast } from 'sonner';
import {
  sendMessage,
  markConversationRead,
  acceptConversation,
  declineConversation,
  deleteConversation as deleteConv,
  reportMessage,
} from '../lib/api';
import {
  ChevronLeft,
  Send,
  Image as ImageIcon,
  Loader2,
  MoreVertical,
  Trash2,
  Flag,
  Check,
  X,
  ChefHat,
  Footprints,
} from 'lucide-react';

const MAX_IMAGE_BYTES = 6 * 1024 * 1024;

export default function Conversation() {
  const navigate = useNavigate();
  const { id: conversationId } = useParams();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useTranslation();
  const qc = useQueryClient();

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportingMsgId, setReportingMsgId] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const fileInputRef = useRef(null);
  const listEndRef = useRef(null);
  const inputRef = useRef(null);

  const { data, isLoading } = useConversation(conversationId, {
    refetchInterval: 4000,
  });
  const conv = data?.conversation;
  const messages = data?.messages || [];
  const peer = conv?.other_user;
  const isPending = conv?.status === 'pending';
  const isInitiator = conv?.initiator_id === user?.user_id;

  // Push-driven instant refresh: when a new message lands while the
  // chat is already open we want it visible immediately, not 4 s later.
  const onPush = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['messages', 'conversation', conversationId] });
    qc.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
  }, [qc, conversationId]);
  usePushBus(onPush);

  // Mark as read on open + every time new messages arrive while the
  // screen is mounted (window focused).
  useEffect(() => {
    if (!conversationId) return;
    if (!conv || conv.unread_count === 0) return;
    markConversationRead(conversationId)
      .then(() => {
        qc.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
        qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
      })
      .catch(() => {});
  }, [conversationId, conv?.unread_count, qc]);

  // Auto-scroll to bottom when messages length changes.
  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
  }, [messages.length]);

  const canSend = !isPending || isInitiator; // recipients of pending must accept first

  const handleSend = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await sendMessage({
        recipient_id: peer?.user_id,
        type: 'text',
        text: body,
      });
      setText('');
      qc.invalidateQueries({ queryKey: ['messages', 'conversation', conversationId] });
      qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
      inputRef.current?.focus();
    } catch (err) {
      const msg = err?.response?.data?.detail || t('messages.rateLimit');
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleAttach = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t('messages.imageTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result;
      setSending(true);
      try {
        await sendMessage({
          recipient_id: peer?.user_id,
          type: 'image',
          text: text.trim() || '',
          image_base64: base64,
        });
        setText('');
        qc.invalidateQueries({ queryKey: ['messages', 'conversation', conversationId] });
        qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
      } catch (err) {
        const msg = err?.response?.data?.detail || t('messages.rateLimit');
        toast.error(msg);
      } finally {
        setSending(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAccept = async () => {
    try {
      await acceptConversation(conversationId);
      qc.invalidateQueries({ queryKey: ['messages', 'conversation', conversationId] });
      qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleDecline = async () => {
    if (!window.confirm(t('messages.declineConfirm'))) return;
    try {
      await declineConversation(conversationId);
      qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
      navigate('/app/messaggi');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteConv = async () => {
    if (!window.confirm(t('messages.deleteConfirm'))) return;
    try {
      await deleteConv(conversationId);
      qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
      navigate('/app/messaggi');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const submitReport = async () => {
    if (!reportingMsgId || !reportReason.trim()) return;
    try {
      await reportMessage(reportingMsgId, reportReason.trim());
      toast.success(t('messages.reportSent'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setReportingMsgId(null);
      setReportReason('');
    }
  };

  if (isLoading || !conv) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDark ? 'bg-[#080D1A]' : 'bg-white'
        }`}
        data-testid="conversation-loading"
      >
        <Loader2 className="w-8 h-8 animate-spin text-[#E8334A]" />
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col h-screen ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`}
      data-testid="conversation-page"
    >
      {/* Sticky header */}
      <div
        className={`flex-shrink-0 z-30 backdrop-blur-md ${
          isDark ? 'bg-[#080D1A]/85 border-b border-white/10' : 'bg-white/90 border-b border-gray-100'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-3 h-14 px-3">
          <button
            onClick={() => navigate('/app/messaggi')}
            data-testid="conversation-back-btn"
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDark ? 'bg-white/10' : 'bg-gray-100'
            }`}
          >
            <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
          </button>
          <button
            onClick={() => peer?.user_id && navigate(`/app/utente/${peer.user_id}`)}
            className="flex items-center gap-3 flex-1 min-w-0 text-left"
            data-testid="conversation-peer"
          >
            <Avatar src={peer?.picture} name={peer?.name || peer?.username} isDark={isDark} small />
            <div className="min-w-0">
              <h2
                className={`font-display text-base truncate ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}
              >
                {peer?.name || peer?.username || '…'}
              </h2>
              {peer?.username && peer?.name && (
                <p className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  @{peer.username}
                </p>
              )}
            </div>
          </button>
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="conversation-menu-btn"
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDark ? 'bg-white/10' : 'bg-gray-100'
              }`}
            >
              <MoreVertical className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
            </button>
            {menuOpen && (
              <div
                className={`absolute right-0 top-12 min-w-[200px] rounded-xl shadow-lg z-50 overflow-hidden ${
                  isDark ? 'bg-[#0D1424] border border-white/10' : 'bg-white border border-gray-200'
                }`}
                onMouseLeave={() => setMenuOpen(false)}
              >
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    handleDeleteConv();
                  }}
                  data-testid="conv-delete-btn"
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm ${
                    isDark ? 'text-white hover:bg-white/10' : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                  {t('messages.delete')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pending banner */}
      {isPending && (
        <div
          className={`flex-shrink-0 px-4 py-3 border-b ${
            isDark
              ? 'bg-amber-500/10 border-amber-500/20 text-amber-300'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
          data-testid="pending-banner"
        >
          <p className="text-sm">
            {isInitiator ? t('messages.sentRequestNote') : t('messages.pendingNote')}
          </p>
          {!isInitiator && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleAccept}
                data-testid="accept-request-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium bg-[#16A34A] text-white"
              >
                <Check className="w-4 h-4" />
                {t('messages.accept')}
              </button>
              <button
                onClick={handleDecline}
                data-testid="decline-request-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-medium bg-red-500 text-white"
              >
                <X className="w-4 h-4" />
                {t('messages.decline')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Messages list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="messages-list">
        {messages.length === 0 ? (
          <div className={`text-center mt-20 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            <p className="text-sm">{t('messages.empty')}</p>
          </div>
        ) : (
          messages.map((m, idx) => (
            <MessageBubble
              key={m.message_id}
              msg={m}
              isOwn={m.sender_id === user?.user_id}
              isDark={isDark}
              language={language}
              t={t}
              prevSenderId={idx > 0 ? messages[idx - 1].sender_id : null}
              onReport={() => setReportingMsgId(m.message_id)}
            />
          ))
        )}
        <div ref={listEndRef} />
      </div>

      {/* Composer */}
      <div
        className={`flex-shrink-0 border-t px-3 py-2 ${
          isDark ? 'bg-[#080D1A] border-white/10' : 'bg-white border-gray-100'
        }`}
        style={{ paddingBottom: 'calc(23px + env(safe-area-inset-bottom, 0px))' }}
      >
        {canSend ? (
          <div className="flex items-end gap-2">
            <button
              onClick={handleAttach}
              disabled={sending}
              data-testid="composer-attach-btn"
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                isDark ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-600'
              } ${sending ? 'opacity-50' : ''}`}
              aria-label={t('messages.attach')}
            >
              <ImageIcon className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
              className="hidden"
              data-testid="composer-file-input"
            />
            <textarea
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t('messages.placeholder')}
              rows={1}
              data-testid="composer-text-input"
              className={`flex-1 resize-none px-4 py-2.5 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8334A]/40 ${
                isDark
                  ? 'bg-white/5 text-white placeholder-white/40'
                  : 'bg-gray-100 text-gray-900 placeholder-gray-400'
              }`}
              style={{ maxHeight: 120 }}
            />
            <button
              onClick={handleSend}
              disabled={!text.trim() || sending}
              data-testid="composer-send-btn"
              aria-label={t('messages.send')}
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-opacity ${
                text.trim() && !sending ? 'bg-[#E8334A] text-white' : 'bg-gray-300 text-gray-500'
              }`}
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        ) : (
          <p
            className={`text-center text-xs py-3 ${isDark ? 'text-white/50' : 'text-gray-500'}`}
          >
            {t('messages.pendingNote')}
          </p>
        )}
      </div>

      {/* Report modal */}
      {reportingMsgId && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-end justify-center"
          onClick={() => setReportingMsgId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-t-3xl p-6 ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
            style={{ paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))' }}
            data-testid="report-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <Flag className="w-5 h-5 text-red-500" />
              <h3 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('messages.report')}
              </h3>
            </div>
            <textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder={t('messages.reportPlaceholder')}
              rows={4}
              maxLength={300}
              data-testid="report-reason-input"
              className={`w-full p-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40 ${
                isDark
                  ? 'bg-white/5 text-white placeholder-white/40'
                  : 'bg-gray-100 text-gray-900 placeholder-gray-400'
              }`}
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setReportingMsgId(null)}
                className={`flex-1 py-3 rounded-xl font-medium ${
                  isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {t('common.cancel', 'Annulla')}
              </button>
              <button
                onClick={submitReport}
                disabled={!reportReason.trim()}
                data-testid="report-submit-btn"
                className={`flex-1 py-3 rounded-xl font-medium text-white ${
                  reportReason.trim() ? 'bg-red-500' : 'bg-red-500/40'
                }`}
              >
                {t('messages.report')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ msg, isOwn, isDark, language, t, prevSenderId, onReport }) {
  const navigate = useNavigate();
  const sameSender = prevSenderId === msg.sender_id;
  const time = useMemo(() => {
    const d = new Date(msg.created_at);
    return d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
  }, [msg.created_at, language]);

  const isShare = msg.type === 'recipe_share' || msg.type === 'workout_share';

  return (
    <div
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${sameSender ? 'mt-0.5' : 'mt-2'}`}
      data-testid={`message-bubble-${msg.message_id}`}
    >
      <div
        onContextMenu={(e) => {
          if (!isOwn) {
            e.preventDefault();
            onReport();
          }
        }}
        onDoubleClick={() => {
          if (!isOwn) onReport();
        }}
        className={`relative max-w-[78%] ${
          isShare ? 'p-1.5' : 'px-3 py-2'
        } rounded-2xl text-sm break-words whitespace-pre-wrap ${
          isOwn
            ? 'bg-[#E8334A] text-white rounded-br-md'
            : isDark
            ? 'bg-white/10 text-white rounded-bl-md'
            : 'bg-gray-100 text-gray-900 rounded-bl-md'
        }`}
      >
        {msg.type === 'image' && msg.image_url && (
          <img
            src={msg.image_url}
            alt={t('messages.photo')}
            className="rounded-xl mb-1 max-w-full max-h-80 object-cover"
          />
        )}

        {/* Rich share preview — recipe / workout. Tapping the card
            navigates to the recipe detail when we still have the id;
            otherwise it's a read-only snapshot. */}
        {isShare && (
          <SharePreviewCard
            msg={msg}
            isOwn={isOwn}
            isDark={isDark}
            t={t}
            onOpen={() => {
              if (msg.type === 'recipe_share') {
                navigate('/app/ricetta', { state: { recipe: msg.data } });
              }
            }}
          />
        )}

        {msg.text && !isShare && <span>{msg.text}</span>}
        {msg.text && isShare && (
          <p className={`px-2 pt-1 pb-0 text-sm ${isOwn ? 'text-white' : ''}`}>{msg.text}</p>
        )}
        <span
          className={`block text-[10px] mt-1 text-right ${
            isShare ? 'px-2 pb-1' : ''
          } ${isOwn ? 'text-white/70' : isDark ? 'text-white/40' : 'text-gray-400'}`}
        >
          {time}
          {isOwn && msg.read_at ? ' ✓✓' : isOwn ? ' ✓' : ''}
        </span>
      </div>
    </div>
  );
}

/**
 * Tappable preview card for a shared recipe (or workout in the future).
 * Renders the snapshot stored in `msg.data` so the card keeps working
 * even if the source recipe was later deleted by its author.
 */
function SharePreviewCard({ msg, isOwn, isDark, t, onOpen }) {
  const d = msg.data || {};
  const isRecipe = msg.type === 'recipe_share';
  const Icon = isRecipe ? ChefHat : Footprints;
  const title = d.nome || d.title || (isRecipe ? t('messages.recipe', 'Ricetta') : t('messages.workout', 'Camminata'));
  return (
    <button
      onClick={onOpen}
      data-testid={`share-preview-${msg.message_id}`}
      className={`block w-64 max-w-full rounded-xl overflow-hidden text-left transition-transform active:scale-[0.98] ${
        isOwn ? 'bg-white/15' : isDark ? 'bg-black/20' : 'bg-white'
      }`}
    >
      {d.image_url ? (
        <img
          src={d.image_url}
          alt={title}
          className="w-full h-32 object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : (
        <div
          className={`w-full h-20 flex items-center justify-center ${
            isOwn ? 'bg-white/10' : isDark ? 'bg-white/5' : 'bg-gray-200'
          }`}
        >
          <Icon className={`w-10 h-10 ${isOwn ? 'text-white/80' : isDark ? 'text-white/40' : 'text-gray-400'}`} />
        </div>
      )}
      <div className="p-2.5">
        <div className="flex items-center gap-1.5 mb-1">
          <Icon className={`w-3.5 h-3.5 ${isOwn ? 'text-white/70' : 'text-[#E8334A]'}`} />
          <span
            className={`text-[10px] uppercase tracking-wide font-semibold ${
              isOwn ? 'text-white/70' : isDark ? 'text-white/50' : 'text-gray-500'
            }`}
          >
            {isRecipe ? t('messages.recipe', 'Ricetta') : t('messages.workout', 'Camminata')}
          </span>
        </div>
        <p
          className={`font-semibold text-sm leading-tight line-clamp-2 ${
            isOwn ? 'text-white' : isDark ? 'text-white' : 'text-gray-900'
          }`}
        >
          {title}
        </p>
        {isRecipe && (d.calorie || d.tempo_preparazione) && (
          <p
            className={`text-[11px] mt-1 ${
              isOwn ? 'text-white/80' : isDark ? 'text-white/60' : 'text-gray-500'
            }`}
          >
            {d.tempo_preparazione ? `⏱ ${d.tempo_preparazione}` : ''}
            {d.tempo_preparazione && d.calorie ? ' · ' : ''}
            {d.calorie ? `🔥 ${d.calorie} kcal` : ''}
          </p>
        )}
      </div>
    </button>
  );
}

function Avatar({ src, name, isDark, small }) {
  const size = small ? 'w-9 h-9' : 'w-12 h-12';
  if (src) {
    return <img src={src} alt={name} className={`${size} rounded-full object-cover flex-shrink-0`} />;
  }
  const initial = (name || '?').charAt(0).toUpperCase();
  return (
    <div
      className={`${size} rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
        isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'
      }`}
    >
      {initial}
    </div>
  );
}
